// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createAxios } from 'ts/createAxios';
import {
  getFormatCurrentDate,
  getFormatCurrentDateTime,
  getFormatDateWithCurrentTimezone,
} from 'ts/formatDate';
import {
  DATE_FORMAT,
  DEFAULT_PAGE,
  LAYOUT_BREAK_POINTS,
} from 'util/ConstantValues';
import {
  ByTenant,
  CollectHistory,
  CollectHistoryListResponse,
  EdiStatus,
} from 'util/Types';
import styles from './HistoryCollected.module.scss';
import EdiStatusTag from 'components/atoms/EdiStatusTag';
import { OperatorType, RoleType } from 'util/Enums';
import {
  getCollectTenantMapper,
  mergeClasses,
  normalizeNumber,
} from 'util/commons';
import { IconDownload } from 'components/atoms/icons/IconDownload';
import { getCollectHistoryCSVDataGroupedByTenant } from 'util/csvExportUtils';
import { handleError } from 'util/errorHandler';
import CustomPagination from 'components/atoms/CustomPagination';
import {
  deleteCollectsData,
  getCollectDeliveries,
  updateCollectData,
} from 'apis/collects';
import CollectDeliveriesDataModal from 'components/molecules/CollectDeliveriesDataModal';
import ResponsiveHistoryTable, {
  CustomColumn,
} from 'components/molecules/ResponsiveHistoryTable';
import CustomCSVLink from 'components/atoms/CustomCsvLink';
import { CustomCheckBox } from 'components/atoms/CustomCheckbox';
import { IconEdit } from 'components/atoms/icons/IconEdit';
import CollectDateSelectModal from 'components/molecules/CollectDateSelectModal';
import dayjs from 'dayjs';
import DeleteItemDarkModal from 'components/molecules/DeleteItemDarkModal';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import IconCalendarTablet from 'components/atoms/icons/IconCalendarTablet';
import { toast } from 'react-toastify';
import { useGetWindowSize } from 'util/hooks/useGetWindowSize';
import { SelectField, SelectOption } from 'components/molecules/SelectField';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { getUsers } from 'apis/operator_site/siteApi';
import { Form } from 'react-final-form';

export type Props = {
  id?: string;
};

export type HistoryCollectedProps = {
  onDateRangeChange?: (value: [string, string] | undefined) => void;
  onDriverIdChange?: (value: string | undefined) => void;
};

export const HistoryCollected = ({
  onDateRangeChange,
  onDriverIdChange,
}: HistoryCollectedProps = {}) => {
  const { t } = useTranslation();
  const [collectedData, setCollectedData] = useState<CollectHistory[]>([]);
  const [pageMeta, setPageMeta] = useState<{
    page: number;
    total: number;
  }>({
    page: 1,
    total: 0,
  });
  const [emissionDetailCollectId, setEmissionDetailCollectId] =
    useState<number>();
  const [exportCsvCollectData, setExportCsvCollectData] = useState<{
    id: number;
    targetIds: string[];
    byTenant: ByTenant;
    wasteUnitName: string | undefined;
  }>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const csvRef = useRef<any>(null);
  const [dateRange, setDateRange] = useState<[string, string]>();
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [editingData, setEditingData] = useState<{
    id: number;
    date?: string;
  }>();
  const { windowWidth } = useGetWindowSize();
  const user = useSelector(selectUser);
  const [errorDeliveryIds, setErrorDeliveryIds] = useState<number[]>([]);
  const [driverOptions, setDriverOptions] = useState<SelectOption[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<
    string | undefined
  >();

  const isAdminOperatorOrAdminSite = useMemo(
    () =>
      user.role === RoleType.AdminOperator || user.role === RoleType.AdminSite,
    [user.role]
  );

  const pageCount = Math.ceil(pageMeta.total / 50);

  const callApi = async (page = pageMeta.page) => {
    try {
      const res = await createAxios({
        prefix: 'v2',
      }).get<CollectHistoryListResponse>('/work-histories/collect/receives', {
        params: {
          page,
          take: 50,
          ...(dateRange && {
            dateFrom: dayjs(dateRange[0]).format(DATE_FORMAT.full_date),
            dateTo: dayjs(dateRange[1]).format(DATE_FORMAT.full_date),
          }),
          ...(selectedDriverId && {
            deliveryUserIds: selectedDriverId,
          }),
        },
      });

      setCollectedData(res.data.deliveries);
      setPageMeta({
        page: res.data.page,
        total: res.data.total,
      });
    } catch (e) {
      handleError(e);
    }
  };

  const fetchDrivers = async () => {
    await getUsers({
      params: {
        take: Number.MAX_SAFE_INTEGER,
        screen: 'workHistoryCollectReceived',
      },
      successCallback: (data) => {
        setDriverOptions(
          data.users.map(({ id, name }) => ({
            label: `${name} (${id})`,
            value: id,
          }))
        );
      },
    });
  };

  const handlePageClick = (selectedItem: { selected: number }) => {
    callApi(selectedItem.selected + 1);
  };

  const handleExportCSV = async (collect: CollectHistory) => {
    await getCollectDeliveries({
      deliveryIds: [collect.id],
      cursor: null,
      successCallback: (data) => {
        setExportCsvCollectData({
          id: collect.id,
          targetIds: data
            .map((item) => item.processingObjectId)
            .filter(Boolean),
          byTenant: getCollectTenantMapper(data)[collect.id] || {},
          wasteUnitName: data.find(
            ({ deliveryId }) => deliveryId.toString() === collect.id.toString()
          )?.wasteUnitName,
        });

        setTimeout(() => {
          csvRef.current?.link?.click();
          setExportCsvCollectData(undefined);
        }, 100);
      },
      failedCallback: () => {
        setExportCsvCollectData(undefined);
      },
    });
  };

  useEffect(() => {
    callApi(DEFAULT_PAGE);
  }, [dateRange, selectedDriverId]);

  useEffect(() => {
    if (isAdminOperatorOrAdminSite) {
      fetchDrivers();
    }
  }, [isAdminOperatorOrAdminSite]);

  useEffect(() => {
    setCheckedIds([]);
    setErrorDeliveryIds([]);
  }, [collectedData]);

  const canItemBeEdited = (item: CollectHistory) => {
    return item.isModifiable;
  };

  const checkableData = useMemo(
    () => collectedData.filter(canItemBeEdited),
    [collectedData]
  );

  const handleCheckItem = (id: number) => {
    if (checkedIds.includes(id)) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
  };

  const handleSelectAll = () => {
    if (!checkableData.length) {
      return;
    }

    if (checkedIds.length === checkableData.length) {
      setCheckedIds([]);
    } else {
      setCheckedIds(checkableData.map((item) => item.id));
    }
  };

  const handleUpdateCollectDate = (id: number, date: string) => {
    updateCollectData({
      id,
      receivedDatetime: date
        ? dayjs(date, DATE_FORMAT.slaYMDHm).format(DATE_FORMAT.full_date)
        : date,
      successCallback: () => {
        toast.success(t('messages.M_011'));
        callApi();
        setEditingData(undefined);
      },
    });
  };

  const handleDeleteCollectsData = () => {
    if (!checkedIds.length) {
      return;
    }

    setErrorDeliveryIds([]);

    deleteCollectsData({
      deliveryIds: checkedIds,
      successCallback: () => {
        callApi();
        setCheckedIds([]);
        setCancelConfirm(false);
      },
      failedCallback: (e) => {
        if (e?.response?.data?.deliveryIds?.length) {
          setErrorDeliveryIds(e.response.data.deliveryIds);

          setTimeout(() => {
            const errRows = document.querySelectorAll(`.${styles.errorRow}`);

            if (errRows.length > 0) {
              const firstErrRow = errRows[0] as HTMLElement;
              firstErrRow.scrollIntoView({
                behavior: 'smooth',
              });
            }
          }, 200);
        }
      },
    });
  };

  const columns: CustomColumn<CollectHistory>[] = useMemo(
    () => [
      {
        Header: (
          <span
            onClick={handleSelectAll}
            style={{
              cursor: checkableData.length > 0 ? 'pointer' : 'not-allowed',
              color: checkableData.length > 0 ? 'inherit' : 'gray',
            }}
          >
            {t(
              checkableData.length === checkedIds.length &&
                checkableData.length > 0
                ? 'common.deselect_all'
                : 'common.select_all'
            )}
          </span>
        ),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accessor: 'checkbox' as any,
        className: styles.checkboxColumn,
        Cell: ({ row }) => {
          if (!canItemBeEdited(row.original)) {
            return null;
          }

          return (
            <CustomCheckBox
              id={row.original.id}
              darkMode
              checked={checkedIds.includes(row.original.id)}
              onChange={() => handleCheckItem(row.original.id)}
            />
          );
        },
      },
      {
        Header: t('common_history_field.date_time') as string,
        accessor: (row) => row.receivedDatetime,
        Cell: ({ value }) => (
          <>{getFormatDateWithCurrentTimezone(value, DATE_FORMAT.slaYMDHm)}</>
        ),
      },
      {
        Header: t('common_history_field.responsible_person') as string,
        accessor: (row) => row.userId,
        Cell: ({ row }) => (
          <>{row.original.userId + '(' + row.original.userName + ')'}</>
        ),
      },
      {
        Header: t('common_history_field.place') as string,
        accessor: (row) => row.areaName,
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('common_history_field.material_name') as string,
        accessor: (row) => row.description,
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('common_history_field.item') as string,
        accessor: (row) => row.wasteUnitName || '-',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('common_history_field.emission_registered_weight') as string,
        accessor: (row) => row.weight,
        Cell: ({ value }) => <>{normalizeNumber({ value }) + 'kg'}</>,
      },
      {
        Header: t('common_history_field.label_manifest_number') as string,
        accessor: (row) => row.ediNumber || '-',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('common_history_field.label_edi_id') as string,
        accessor: (row) => row.receivedEdiStatus || EdiStatus.None,
        Cell: ({ row }) => (
          <>
            {row.original.receivedEdiStatus != null &&
            row.original.receivedEdiStatus !== EdiStatus.None ? (
              <EdiStatusTag
                status={row.original.receivedEdiStatus}
                operatorType={OperatorType.Collects}
              />
            ) : (
              '-'
            )}
          </>
        ),
      },
      {
        Header: '',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accessor: 'actions' as any,
        Cell: ({ row }) => (
          <div className={styles.actionCell}>
            {canItemBeEdited(row.original) && (
              <div
                className={styles.edit}
                onClick={() =>
                  setEditingData({
                    id: row.original.id,
                    date: row.original.receivedDatetime
                      ? getFormatDateWithCurrentTimezone(
                          row.original.receivedDatetime,
                          DATE_FORMAT.slaYMDHm
                        )
                      : undefined,
                  })
                }
              >
                <IconEdit size={32} stroke="white" />
              </div>
            )}

            <button
              onClick={() => setEmissionDetailCollectId(row.original.id)}
              className={styles.viewButton}
            >
              {t('common.button.view_details')}
            </button>

            <div
              className={styles.download}
              onClick={() => handleExportCSV(row.original)}
            >
              <IconDownload strokeColor="black" />
            </div>

            {exportCsvCollectData != null &&
              exportCsvCollectData?.id === row.original.id && (
                <CustomCSVLink
                  className={styles.csvLink}
                  ref={csvRef}
                  filename={`${t(
                    'histories.csv_file_name_collect_by_waste_unit'
                  )}_${getFormatCurrentDate()}`}
                  data={getCollectHistoryCSVDataGroupedByTenant({
                    type: 'received',
                    data: row.original,
                    targetIds: exportCsvCollectData.targetIds,
                    byTenant: exportCsvCollectData.byTenant,
                    exportDatetimeString: getFormatCurrentDateTime(),
                    wasteUnitName: exportCsvCollectData.wasteUnitName,
                    t,
                  })}
                />
              )}
          </div>
        ),
      },
    ],
    [t, exportCsvCollectData, checkedIds]
  );

  return (
    <div className={styles.historyCollected}>
      <div className={styles.historyCollectedHeader}>
        {windowWidth <= LAYOUT_BREAK_POINTS.MD && (
          <span
            onClick={handleSelectAll}
            style={{
              cursor: checkableData.length > 0 ? 'pointer' : 'not-allowed',
              color: checkableData.length > 0 ? 'white' : 'gray',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {t(
              checkableData.length === checkedIds.length &&
                checkableData.length > 0
                ? 'common.deselect_all'
                : 'common.select_all'
            )}
          </span>
        )}

        <div className={styles.filterContainer}>
          <TableFilterCalendar
            wrapperClassName={styles.dateFilter}
            expanded={{
              placeholder: t('common.placeholder.select_date_range'),
              icon: <IconCalendarTablet />,
            }}
            portalPositionPoint={(ref) => ref}
            endDateRequired
            value={dateRange}
            onChange={(value) => {
              setDateRange(value);
              onDateRangeChange?.(value);
            }}
            align={windowWidth <= LAYOUT_BREAK_POINTS.MD ? 'right' : 'left'}
          />

          {isAdminOperatorOrAdminSite && (
            <Form onSubmit={() => {}}>
              {() => (
                <SelectField
                  containerClassName={styles.driverFilter}
                  name="driverId"
                  options={driverOptions}
                  placeholder={t('collects.collection_registered_user')}
                  value={selectedDriverId}
                  onChange={(value) => {
                    setSelectedDriverId(value?.toString());
                    onDriverIdChange?.(value?.toString());
                  }}
                  isDarkmode
                  allowClear
                  showPlaceholderOption={true}
                />
              )}
            </Form>
          )}
        </div>
      </div>

      <div className={styles.historyCollectedContent}>
        <ResponsiveHistoryTable<CollectHistory>
          data={collectedData}
          columns={columns}
          scrollResetDependencies={[pageMeta]}
          className={styles.responsiveTable}
          rowClassName={(rowData) =>
            errorDeliveryIds.includes(rowData.id) ? styles.errorRow : ''
          }
        />
      </div>

      {pageMeta.total > 0 && (
        <div className={styles.paginationContainer}>
          <button
            className={mergeClasses(styles.cancelCollectBtn, {
              [styles.cancelCollectBtnDisabled]: !checkedIds.length,
            })}
            onClick={() => checkedIds.length > 0 && setCancelConfirm(true)}
          >
            {t('collects.cancel_collect_data')}
          </button>

          <CustomPagination
            pageCount={pageCount}
            onPageChange={handlePageClick}
            forcePage={pageMeta.page - 1}
          />
        </div>
      )}

      <CollectDeliveriesDataModal
        collectId={emissionDetailCollectId}
        onClose={() => {
          setEmissionDetailCollectId(undefined);
        }}
      />

      {editingData != null && (
        <CollectDateSelectModal
          state={[
            editingData.date,
            (date) =>
              setEditingData(
                (prev) =>
                  prev && {
                    ...prev,
                    date,
                  }
              ),
          ]}
          onClose={() => setEditingData(undefined)}
          onSubmitAndFinish={(date) =>
            handleUpdateCollectDate(editingData.id, date || '')
          }
          title={t('collects.edit_collect_data') || ''}
          saveButtonLabel={t('route_list.edit_btn') || ''}
          description={t('collect_registration.select_received_date') || ''}
        />
      )}

      {cancelConfirm && checkedIds.length > 0 && (
        <DeleteItemDarkModal
          onClose={() => setCancelConfirm(false)}
          onHandleDelete={handleDeleteCollectsData}
          content={t('collects.cancel_confirm')}
          deleteBtnLabel={t('collects.btn_confirm_cancel') || ''}
        />
      )}
    </div>
  );
};
